import { Injectable } from '@angular/core'
import { Subject, Subscription } from 'rxjs'
import { NavigationHelper } from '../helpers/navigation';
import { HistoryTrackerService } from './history-tracker.service';

@Injectable({
	providedIn: 'root'
})
export class NavigationEventService {
	private navigationEventSubject = new Subject<{event: PopStateEvent, isBackNavigation: boolean}>();
	public navigationEvent$ = this.navigationEventSubject.asObservable();
	private hasActiveSubscription = false;

	constructor(
		private navigationHelper: NavigationHelper,
		private historyTrackerService: HistoryTrackerService) {

		// Manually determine if we are going back or forward based off the history.
		// The history is tracked everywhere in the app configured in the app.component.ts.
		// This event listener / history interaction is to account for the browser back and forward buttons.
		// While the nav buttons themselves use the router lib.
		window.addEventListener('popstate', (event) => {
			const nextUrl = window.location.pathname;
			const previousUrl = this.historyTrackerService.RemoveUrlHistory();

			const goingToComponentIndex = this.navigationHelper.componentMapping.find(([_, v]) => v === nextUrl)?.[0];
			const previousComponentIndex = this.navigationHelper.componentMapping.find(([_, v]) => v === previousUrl)?.[0];
			
			// If the component mapping index is not found, it means the destination url is not in the "add note" flow, so just proceed like normal
			if (goingToComponentIndex === undefined || previousComponentIndex === undefined) {
				return;
			}

			// Otherwise we are in the add note flow, so we want to process downstream like normal (which is in the nav button component)
			const isBackNavigation = goingToComponentIndex < previousComponentIndex;

			// Edge case:
			// State:
			// 	1. Note submitted
			// 	2. Browser back button pushed
			// Note: 
			//	- Recall the last component in the flow blocks pointer events such that only the "head to guestbook" button is enabled.
			//	- (To block spamming the same note many times)
			// Solution: 
			//  If the above is met, we are still blocked and thus no touches work on the app, a major error. 
			//	Then anually enable the pointer control state
			if (isBackNavigation) {
				document.body.style.pointerEvents = 'auto';
			 }

			this.navigationEventSubject.next({event, isBackNavigation: isBackNavigation});
		});
	}

	public subscribeToPopState(callback: ({event, isBackNavigation}) => void): Subscription | null {
		if (!this.hasActiveSubscription) {
			this.hasActiveSubscription = true;
			return this.navigationEvent$.subscribe(callback);
		}
		return null;
	}

	public unsubscribeFromPopState(subscription: Subscription) {
		if (subscription) {
			subscription.unsubscribe();
			this.hasActiveSubscription = false;
		}
	}
}